import { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react';

import { useFeatureFlags } from '@Contexts/FeatureFlags';
import * as PageProductStore from '@Stores/PageProduct';
import useMedias from '@Hooks/useMedias';
import VideoConsulationBanner from '../../../VideoConsulationBanner';
import ConfiguratorTabContainer from '../ConfiguratorTab/ConfiguratorTabContainer';
import styles from './Configurator.module.css';

import type { FiltratorId } from '@Stores/Filtrator';
import type { HTMLAttributes, MouseEvent, MutableRefObject } from 'react';
import type { ConfiguratorData, ConfiguratorTab } from '@Pages/PageProduct/typings';
import type { ChangeConfigurationProps } from '../../Sidebar';

export interface ConfiguratorProps extends HTMLAttributes<HTMLDivElement> {
  configurator: ConfiguratorData;
  className?: string;
  entityId: FiltratorId;
  needVideoConsultation?: boolean;
  onClickItem?: (e: MouseEvent, item: ChangeConfigurationProps) => void;
  sidebarRef: MutableRefObject<HTMLDivElement>;
}

const Configurator = ({
  configurator,
  entityId,
  needVideoConsultation,
  onClickItem,
  sidebarRef,
  ...restProps
}: ConfiguratorProps) => {
  const { tabs } = configurator;
  const { videoconsultationAvailable } = useFeatureFlags();
  const [activeTabs, setActiveTabs] = useState<ConfiguratorTab[]>([]);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { isMobileM } = useMedias();

  const page = PageProductStore.useData();
  const { readySolutions } = page;

  const handleClickTab = useCallback(
    (tab: ConfiguratorTab) => {
      setActiveTabs((prev) => {
        const exists = prev.some((existingTab) => existingTab.id === tab.id);

        if (exists) {
          return prev.filter((existingTab) => existingTab.id !== tab.id);
        }
        return [tab];
      });

      // Логика прокрутки
      const currentIndex = tabs.findIndex((t) => t.id === activeTabs[0]?.id);
      const newIndex = tabs.findIndex((t) => t.id === tab.id);

      if (currentIndex === -1) return;

      if (newIndex > currentIndex && tabRefs.current[newIndex]) {
        const element = isMobileM
          ? tabRefs.current[0]
          : tabRefs.current[newIndex + 1] || sidebarRef?.current;

        setTimeout(() => {
          element?.scrollIntoView({
            behavior: 'smooth',
            block: isMobileM ? 'start' : 'end',
          });
        }, 300);
      }
    },
    [activeTabs, tabs, isMobileM, sidebarRef],
  );

  useEffect(() => {
    if (configurator.tabs?.length < 1) return;

    const activeTab = configurator.tabs.find((tab) => tab.id !== 'modules');
    setActiveTabs([activeTab]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div {...restProps}>
      {tabs?.map((tab, index) => {
        const isActive = activeTabs.some((activeTab) => activeTab?.id === tab.id);

        return (
          <Fragment key={tab.id}>
            <div
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              className={styles.tab}
              onClick={tab.onClick}
            >
              <ConfiguratorTabContainer
                tab={tab}
                entityId={entityId}
                isActive={isActive}
                needVideoConsultation={needVideoConsultation}
                canOrderSamples={readySolutions.canOrderSamples}
                onClickTab={() => (tab.onClick ? tab.onClick : handleClickTab(tab))}
                onClickItem={onClickItem}
              />
            </div>
            {tab.id === 'upholstery' && videoconsultationAvailable && needVideoConsultation && (
              <VideoConsulationBanner className={styles.banner} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default memo(Configurator);
